/**
 * Background attachment uploader context.
 *
 * Instead of blocking ticket creation / reply on attachment uploads, callers
 * hand files to this context and continue immediately. The ticket detail page
 * reads pending upload state and shows a progress banner.
 *
 * ponytail: single context + Map, no external deps. Retry once on failure.
 * onAllDone callback supports reply-then-send flow (upload → send email).
 *
 * The context value includes a monotonic `version` counter so consumers
 * re-render when upload state changes (ref mutations alone don't trigger
 * child re-renders even with forceRender in the provider).
 */
import * as React from 'react';
import { AttachmentUploadError, type PresignedUpload, uploadAttachment } from './attachments';

// ─── Types ────────────────────────────────────────────────────────────────────

export type UploadStatus = 'uploading' | 'done' | 'error';

export interface FileUploadState {
  file: File;
  status: UploadStatus;
  errorMessage?: string;
  attachmentId?: string;
  conversationId?: string;
  uploadSession?: PresignedUpload;
}

export interface EnqueueOptions {
  conversationId?: string;
  /** Called once when every file in this batch uploads successfully. */
  onAllDone?: () => void;
}

interface BatchMeta {
  startIndex: number;
  count: number;
  conversationId?: string;
  onAllDone?: () => void;
}

interface AttachmentUploaderContextValue {
  /** Enqueue files for background upload to a ticket (optionally a conversation). */
  enqueue: (ticketId: string, files: File[], options?: EnqueueOptions) => void;
  /** Current upload state for a ticket (empty array when nothing pending). */
  getUploads: (ticketId: string) => FileUploadState[];
  /** Retry all failed uploads for a ticket. */
  retryFailed: (ticketId: string) => void;
  /** Dismiss completed/failed uploads for a ticket. */
  dismiss: (ticketId: string) => void;
  /** Monotonic counter — changes whenever upload state changes, forcing consumer re-renders. */
  version: number;
}

const AttachmentUploaderContext = React.createContext<AttachmentUploaderContextValue | null>(null);

// ─── Provider ─────────────────────────────────────────────────────────────────

export function AttachmentUploaderProvider({ children }: { children: React.ReactNode }) {
  const uploadsRef = React.useRef<Map<string, FileUploadState[]>>(new Map());
  const batchesRef = React.useRef<Map<string, BatchMeta[]>>(new Map());
  const [version, bump] = React.useReducer((x: number) => x + 1, 0);

  function setFileState(
    ticketId: string,
    fileIndex: number,
    patch: Partial<FileUploadState>,
  ) {
    const bucket = uploadsRef.current.get(ticketId);
    if (!bucket || !bucket[fileIndex]) return;
    Object.assign(bucket[fileIndex], patch);
    bump();
  }

  function checkBatchDone(ticketId: string) {
    const batches = batchesRef.current.get(ticketId);
    const bucket = uploadsRef.current.get(ticketId);
    if (!batches || !bucket) return;

    const remaining: BatchMeta[] = [];
    for (const batch of batches) {
      const slice = bucket.slice(batch.startIndex, batch.startIndex + batch.count);
      const allDone = slice.length === batch.count && slice.every((s) => s.status === 'done');
      if (allDone) {
        try { batch.onAllDone?.(); } catch { /* fire-and-forget */ }
      } else {
        // Keep failed batches registered so retryFailed can complete the
        // upload-before-send flow after a later successful retry.
        remaining.push(batch);
      }
    }
    batchesRef.current.set(ticketId, remaining);
  }

  async function doUpload(
    ticketId: string,
    file: File,
    index: number,
    conversationId?: string,
    isRetry = false,
  ) {
    const currentState = uploadsRef.current.get(ticketId)?.[index];
    setFileState(ticketId, index, { status: 'uploading', errorMessage: undefined });
    try {
      const result = await uploadAttachment(file, { ticketId, conversationId }, currentState?.uploadSession);
      setFileState(ticketId, index, { status: 'done', attachmentId: result.id, uploadSession: undefined });
    } catch (err: unknown) {
      if (err instanceof AttachmentUploadError) {
        setFileState(ticketId, index, { uploadSession: err.session });
      }
      // ponytail: one automatic retry before giving up; an ambiguous S3
      // failure keeps the same presigned key instead of creating an orphan.
      if (!isRetry) {
        await doUpload(ticketId, file, index, conversationId, true);
        return; // retry handled everything including checkBatchDone
      }
      const message = err instanceof Error ? err.message : 'Upload failed';
      setFileState(ticketId, index, { status: 'error', errorMessage: message });
    }
    checkBatchDone(ticketId);
  }

  const enqueue = React.useCallback((ticketId: string, files: File[], options?: EnqueueOptions) => {
    const states: FileUploadState[] = files.map((file) => ({
      file,
      status: 'uploading' as const,
      conversationId: options?.conversationId,
    }));
    const existing = uploadsRef.current.get(ticketId) ?? [];
    const baseIndex = existing.length;
    uploadsRef.current.set(ticketId, [...existing, ...states]);

    if (options?.onAllDone) {
      const batches = batchesRef.current.get(ticketId) ?? [];
      batches.push({
        startIndex: baseIndex,
        count: files.length,
        conversationId: options.conversationId,
        onAllDone: options.onAllDone,
      });
      batchesRef.current.set(ticketId, batches);
    }

    bump();

    files.forEach((file, i) => {
      void doUpload(ticketId, file, baseIndex + i, options?.conversationId);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const getUploads = React.useCallback((ticketId: string): FileUploadState[] => {
    return uploadsRef.current.get(ticketId) ?? [];
  }, []);

  const retryFailed = React.useCallback((ticketId: string) => {
    const bucket = uploadsRef.current.get(ticketId);
    if (!bucket) return;
    bucket.forEach((state, index) => {
      if (state.status === 'error') {
        void doUpload(ticketId, state.file, index, state.conversationId);
      }
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const dismiss = React.useCallback((ticketId: string) => {
    uploadsRef.current.delete(ticketId);
    batchesRef.current.delete(ticketId);
    bump();
  }, []);

  // ponytail: version in the value object forces consumers to re-render when
  // upload state changes — the ref mutations alone don't propagate to children.
  const value = React.useMemo(
    () => ({ enqueue, getUploads, retryFailed, dismiss, version }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [enqueue, getUploads, retryFailed, dismiss, version],
  );

  return (
    <AttachmentUploaderContext.Provider value={value}>
      {children}
    </AttachmentUploaderContext.Provider>
  );
}

// ─── Hook ─────────────────────────────────────────────────────────────────────

export function useAttachmentUploader() {
  const ctx = React.useContext(AttachmentUploaderContext);
  if (!ctx) throw new Error('useAttachmentUploader must be used within AttachmentUploaderProvider');
  return ctx;
}
